import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { Card, Btn, Empty, Modal } from "../../components/UI";
import { FInput } from "./FormControls";
import { Plus, Search, Pencil, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";

export default function IntakeList() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    const params = { page, limit: 15 };
    if (search) params.search = search;
    api.get("/intake-forms", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [page, search]);

  const doDelete = async () => {
    try {
      await api.delete(`/intake-forms/${confirmDelete.id}`);
      toast.success("Intake deleted");
      setConfirmDelete(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <Card>
        <div className="flex flex-wrap items-center gap-3 p-5">
          <h1 className="font-heading text-lg font-bold text-[#3E2723] mr-2">Client Intakes</h1>
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={16} className="absolute left-3 top-3.5 text-[#84675B]" />
            <FInput data-testid="intake-form-search" placeholder="Search by name…" className="pl-9" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <div className="ml-auto">
            <Btn data-testid="add-new-client-btn" onClick={() => navigate("/engagement/intake/new")} className="rounded-lg"><Plus size={16} /> Add New Client</Btn>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead><tr>{["Name", "Email", "Mobile no", "Account Number", "SSN", "Client Status", "Actions"].map((h) => (
              <th key={h} className="bg-[#EDE7DC] text-[#4E342E] font-semibold py-3 px-4">{h}</th>))}</tr></thead>
            <tbody>
              {data.items.map((r) => {
                const d = r.demographics || {};
                return (
                  <tr key={r.id} data-testid={`intake-form-row-${r.id}`} className="hover:bg-[#FDFCF8]">
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4 font-medium text-[#3E2723]">{d.first_name} {d.last_name}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{d.email || "-"}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{d.cell_phone || d.home_phone || "-"}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{r.account_number}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{d.ssn || "-"}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4 capitalize">{String(d.client_status || "").replace("_", " ") || "-"}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">
                      <div className="flex gap-2">
                        {r.client_id && (
                          <button data-testid={`view-client-${r.id}`} aria-label="Open client" title="Open client record" onClick={() => navigate(`/clients/${r.client_id}`)}
                            className="h-9 w-9 rounded-lg bg-[#F5F2EB] text-[#6D4C41] flex items-center justify-center hover:bg-[#EDE7DC]"><UserRound size={14} /></button>
                        )}
                        <button data-testid={`edit-intake-form-${r.id}`} aria-label="Edit" onClick={() => navigate(`/engagement/intake/${r.id}/edit`)}
                          className="h-9 w-9 rounded-lg bg-[#F5F2EB] text-[#6D4C41] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={14} /></button>
                        <button data-testid={`delete-intake-form-${r.id}`} aria-label="Delete" onClick={() => setConfirmDelete(r)}
                          className="h-9 w-9 rounded-lg bg-[#F5F2EB] text-[#6D4C41] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.items.length === 0 && <Empty text="No client intakes yet - click Add New Client to start" />}
        </div>
        {data.pages > 1 && (
          <div className="flex items-center gap-2 p-5">
            {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
              <button key={p} data-testid={`intake-form-page-${p}`} onClick={() => setPage(p)}
                className={`h-8 w-8 rounded-md text-sm font-semibold ${p === page ? "bg-[#4E342E] text-white" : "text-[#6D4C41] hover:bg-[#F5F2EB]"}`}>{p}</button>
            ))}
          </div>
        )}
      </Card>
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Intake">
        <p className="text-sm text-[#6D4C41] mb-5">Delete the intake for <span className="font-semibold text-[#3E2723]">{confirmDelete?.demographics?.first_name} {confirmDelete?.demographics?.last_name}</span>? The client record will remain.</p>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
          <Btn data-testid="confirm-delete-intake-form-btn" variant="danger" onClick={doDelete}>Delete</Btn>
        </div>
      </Modal>
    </div>
  );
}
